import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Box,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  People,
  ExitToApp,
  Forum,
  ChevronLeft,
} from '@mui/icons-material';
import { authAPI } from '../services/api';

const drawerWidth = 260;

const useStyles = makeStyles()((theme) => ({
  root: { display: 'flex', minHeight: '100vh' },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: '#13202b',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  menuButton: { marginRight: theme.spacing(2) },
  drawer: { width: drawerWidth, flexShrink: 0, whiteSpace: 'nowrap' },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: '#0d1820',
    borderRight: '1px solid rgba(148, 163, 184, 0.1)',
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    backgroundColor: '#0d1820',
    borderRight: '1px solid rgba(148, 163, 184, 0.1)',
    [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(9)} + 1px)` },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    marginTop: 64,
    backgroundColor: theme.palette.background.default,
    minHeight: 'calc(100vh - 64px)',
  },
  title: {
    flexGrow: 1,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  logoIcon: { backgroundColor: theme.palette.primary.main, width: 36, height: 36 },
  userSection: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  userAvatar: {
    width: 32,
    height: 32,
    backgroundColor: theme.palette.primary.main,
    color: '#06222b',
    cursor: 'pointer',
  },
  listItem: {
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5, 1),
    '&.active': {
      backgroundColor: 'rgba(0, 217, 255, 0.12)',
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
      '& .MuiListItemText-primary': {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
    },
    '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.08)' },
  },
  listItemIcon: { color: theme.palette.text.secondary, minWidth: 40 },
  sidebarTitle: { color: theme.palette.text.primary, fontWeight: 700, fontSize: '1rem' },
}));

const TenantAdminLayout = () => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, scriptHash } = useParams();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const user = authAPI.getCurrentUser();
  const membership = authAPI.getMembership();
  const tenant = authAPI.getTenant();

  const base = `/${tenantId}/${scriptHash}`;
  const menuItems = [
    { title: 'Settings', icon: SettingsIcon, path: `${base}/settings` },
    { title: 'Users', icon: People, path: `${base}/users` },
  ];

  const handleLogout = () => {
    authAPI.logout();
    navigate(`${base}/login`);
  };

  const displayName = membership?.username || user?.displayName || user?.email || 'You';

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar} elevation={0}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            edge="start"
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" className={classes.title}>
            <Avatar className={classes.logoIcon}>
              <Forum />
            </Avatar>
            {tenant?.name || 'Chat'} Admin
          </Typography>

          <div className={classes.userSection}>
            {membership?.role && (
              <Chip size="small" label={membership.role} color="primary" variant="outlined" />
            )}
            <Typography variant="body2" color="textSecondary">
              {displayName}
            </Typography>
            <Avatar className={classes.userAvatar} onClick={(e) => setAnchorEl(e.currentTarget)}>
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
          </div>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        className={cx(classes.drawer, drawerOpen ? classes.drawerOpen : classes.drawerClose)}
        classes={{ paper: drawerOpen ? classes.drawerOpen : classes.drawerClose }}
      >
        <div className={classes.toolbar}>
          {drawerOpen && (
            <Box sx={{ flexGrow: 1, pl: 2 }}>
              <Typography className={classes.sidebarTitle}>Chat Admin</Typography>
            </Box>
          )}
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeft />
          </IconButton>
        </div>
        <Divider />
        <List>
          {menuItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <ListItemButton
                key={item.title}
                className={cx(classes.listItem, isActive && 'active')}
                onClick={() => navigate(item.path)}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <item.icon />
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>

      <main className={classes.content}>
        <Outlet />
      </main>
    </div>
  );
};

export default TenantAdminLayout;
